import { cn, Icon } from '@lqa-frontend/ui-system';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { useResolvedPortalContainer } from '../../hooks/use-portal-container';

type GenericDrawerDirection = 'bottom' | 'right';

const GenericDrawerOverlay = ({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) => (
	<DrawerPrimitive.Overlay
		className={cn('fixed inset-0 z-50 bg-black/50', className)}
		{...props}
	/>
);

function GenericDrawerContent({
	className,
	bodyClassName,
	children,
	direction,
	title,
	description,
	contentRef,
	portalContainer,
	footer,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
	direction: GenericDrawerDirection;
	title: string;
	description: string;
	contentRef?: React.Ref<HTMLDivElement>;
	portalContainer?: HTMLElement | null;
	footer?: React.ReactNode;
	bodyClassName?: string;
}) {
	const resolvedContainer = useResolvedPortalContainer(portalContainer);
	return (
		<DrawerPrimitive.Portal container={resolvedContainer}>
			<GenericDrawerOverlay />
			<DrawerPrimitive.Content
				className={cn(
					'pt-24',
					'fixed z-50 flex flex-col bg-base-1 outline-none',
					direction === 'bottom' &&
						'inset-x-0 bottom-0 mt-24 rounded-t-xl h-[calc(100%-3rem)]',
					// `h-[100dvh]` (instead of `h-full`) anchors the drawer's height
					// to the dynamic viewport regardless of whether a transformed
					// ancestor (e.g. Vaul's body-scale) reshapes the containing
					// block of fixed-positioned children. With a tall page behind
					// the drawer, `h-full` was inheriting body height and the
					// drawer's inner overflow container couldn't ever overflow.
					direction === 'right' && 'top-0 right-0 h-[100dvh] w-400',
					className
				)}
				{...props}
			>
				<DialogPrimitive.Title className="sr-only">
					{title}
				</DialogPrimitive.Title>
				<DialogPrimitive.Description className="sr-only">
					{description}
				</DialogPrimitive.Description>

				{direction === 'bottom' && (
					<div className="mx-auto mt-7 h-2 w-100 rounded-max bg-muted" />
				)}

				{/* `min-h-0` is load-bearing: a flex child defaults to `min-height: auto`
			    which prevents it from shrinking below its content size, so
			    `overflow-y-auto + flex-1` silently fails to produce a scrollbar
			    when content exceeds the available space.
			    `overscroll-contain` keeps wheel/touchpad events from chaining
			    to the page behind the drawer when the user reaches an edge. */}
				<div
					ref={contentRef}
					className={cn(
						'overflow-y-auto overscroll-contain flex-1 min-h-0 py-16 px-24',
						bodyClassName
					)}
				>
					{children}
				</div>

				{/* Pinned footer — sits below the scrollable body so action bars
				    stay reachable regardless of content height / scroll. */}
				{footer ? (
					<div className="shrink-0 border-t border-divider px-24 py-16">
						{footer}
					</div>
				) : null}

				<DialogPrimitive.Close className="cursor-pointer absolute top-0 right-0 p-12 opacity-70 hover:opacity-100 transition-opacity text-foreground">
					<Icon name="xLg" size="s" />
				</DialogPrimitive.Close>
			</DrawerPrimitive.Content>
		</DrawerPrimitive.Portal>
	);
}

const GenericDrawer = ({
	shouldScaleBackground,
	direction = 'bottom',
	title = 'Drawer',
	description = 'Drawer content',
	contentClassName,
	bodyClassName,
	contentRef,
	portalContainer,
	footer,
	children,
	modal,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & {
	direction?: GenericDrawerDirection;
	title?: string;
	description?: string;
	contentClassName?: string;
	/** Extra classes for the scrollable body, e.g. to drop its padding. */
	bodyClassName?: string;
	contentRef?: React.Ref<HTMLDivElement>;
	portalContainer?: HTMLElement | null;
	footer?: React.ReactNode;
}) => {
	// Vaul's `shouldScaleBackground` applies `transform: scale(...)` to the
	// document body for the iOS-style "card peeking" effect. It only makes
	// visual sense for bottom drawers — and for any direction it has a
	// nasty side-effect: a transformed ancestor changes the containing
	// block of `position: fixed` descendants. The drawer's own
	// `inset-y-0 h-full` then anchors to the (full-document-height) body
	// instead of the viewport, so when the page behind is taller than the
	// viewport the drawer stretches to body height, its inner
	// `overflow-y-auto` never overflows, and scrolling silently fails.
	// Default to scale only for bottom; let callers opt in / out explicitly.
	const resolvedScale = shouldScaleBackground ?? direction === 'bottom';

	return (
		<DrawerPrimitive.Root
			shouldScaleBackground={resolvedScale}
			direction={direction}
			modal={modal}
			{...props}
		>
			<GenericDrawerContent
				direction={direction}
				title={title}
				description={description}
				className={contentClassName}
				bodyClassName={bodyClassName}
				contentRef={contentRef}
				portalContainer={portalContainer}
				footer={footer}
			>
				{children}
			</GenericDrawerContent>
		</DrawerPrimitive.Root>
	);
};

export { GenericDrawer };
